// React
import React, { Fragment, useState, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import BProductButton, { HostStyles } from './styles'
import Button from './Button'

// Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus as farPlus, faMinus as farMinus } from '@fortawesome/pro-regular-svg-icons'

// Shared
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import { withTranslation } from 'shared/utils/withTranslation'
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import CartActions from 'store/v2/redux/actions/cart'
import ClientActions from 'store/v2/redux/actions/client'
import { triggerUserFrameworkBookEvent } from 'shared/utils/userFrameworkEvents'
import { fetchItem } from 'shop-components/redux/actions/item'
import { hasVariations, hasVariationSelected, isValidBundleVariations } from 'shop-components/utils/variations'

const ProductButton = ({
  item,
  behavior,
  hasDates,
  useAvailability,
  bookProduct,
  fetchItem,
  clearCart,
  setVariationError,
  setBundleError,
  cart,
  t
}) => {
  const [quantity, setQuantity] = useState(1)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(null)

  useEffect(() => {
    fetchItem()
  }, [])

  const bookingId = () => {
    if (item.type === 'bundles') return item.id

    return item.variation_id || item.products[0].id
  }

  const isValidVariant = (item) => {
    if (item.type === 'bundles') {
      if (!isValidBundleVariations(item)) {
        setBundleError(item.id, true)
        return false
      } else return true
    }

    if (!hasVariations(item)) return true
    else if (hasVariationSelected(item)) return true

    setVariationError(item.id, true)
    return false
  }

  const checkoutAction = useCallback(() => {
    if (ClientActions.evaluateCartOverdue() || !hasDates) {
      return ClientActions.toggleModalPicker(true)
    }

    return ClientActions.setCheckingOut()
  }, [hasDates])

  const showCartAction = useCallback(() => ClientActions.toggleCart(true), [])

  const handleBook = useCallback(async () => {
    let timeout
    const id = bookingId()
    if (!isValidVariant(item)) return

    setLoading(true)

    if (behavior === 'checkout' && cart?.created_at) await clearCart()

    await bookProduct({ id, quantity, configuration: item.configuration })
      .then(() => {
        const action = behavior === 'show_cart' ? showCartAction : checkoutAction

        triggerUserFrameworkBookEvent(id, quantity)

        return setTimeout(action, 150)
      })
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'))
      .finally(() => {
        setLoading(false)

        timeout = setTimeout(() => {
          setStatus(null)
        }, 3000)
      })

    return () => {
      clearTimeout(timeout)
    }
  }, [item, quantity, behavior, cart])

  const handleButtonClick = useCallback(() => handleBook(), [handleBook])

  const handleInputFocus = useCallback(({ target }) => target.select(), [])

  const handleChangeQuantity = useCallback(({ target: { value } }) => {
    if (value && !isNaN(value)) setQuantity(parseInt(value))
  }, [])

  const handleIncreaseQuantity = useCallback(() => setQuantity((prev) => prev + 1), [])

  const handleDecreaseQuantity = useCallback(() => {
    setQuantity((prev) => {
      if (prev === 0) return prev

      return prev - 1
    })
  }, [])

  const disabled = useMemo(() => {
    const cannotAddToCart = quantity <= 0 || isNaN(quantity) || loading
    if (behavior === 'checkout' && useAvailability) {
      const available = item?.availability?.plannable

      return cannotAddToCart || (hasDates && !available) || (hasDates && quantity > available)
    }

    return cannotAddToCart
  }, [quantity, loading, behavior, useAvailability, item, hasDates])

  return (
    <Fragment>
      <HostStyles />
      <BProductButton>
        <BProductButton.InputWrapper>
          <BProductButton.Input
            value={quantity}
            onChange={handleChangeQuantity}
            onFocus={handleInputFocus}
            name="quantity"
            type="number"
            className="bq-quantity"
            min="1"
          />
          <BProductButton.Controls>
            <BProductButton.Control onClick={handleIncreaseQuantity}>
              <FontAwesomeIcon icon={farPlus} size="sm" />
            </BProductButton.Control>
            <BProductButton.Control disabled={quantity <= 1} onClick={handleDecreaseQuantity}>
              <FontAwesomeIcon icon={farMinus} size="sm" />
            </BProductButton.Control>
          </BProductButton.Controls>
        </BProductButton.InputWrapper>
        <Button
          label={t('user.store.add_to_cart')}
          onClick={handleButtonClick}
          loading={loading}
          disabled={disabled}
          status={status}
          behavior={behavior}
        />
      </BProductButton>
    </Fragment>
  )
}

ProductButton.propTypes = {
  item: PropTypes.object,
  behavior: PropTypes.oneOf(['show_cart', 'checkout']),
  hasDates: PropTypes.bool,
  useAvailability: PropTypes.bool,
  bookProduct: PropTypes.func,
  clearCart: PropTypes.func,
  fetchItem: PropTypes.func,
  setVariationError: PropTypes.func,
  setBundleError: PropTypes.func,
  cart: PropTypes.object,
  t: PropTypes.func
}

ProductButton.displayName = 'ProductButton'

const mapStateToProps = (state, props) => {
  const cart = state.orm.carts.getById(state.client.cartId)
  const item = state.items.records[props.productId]

  const behavior = state.settings.get('store.behaviors.add_button')
  const useAvailability = state.settings.get('store.use_availability') && item?.tracking_type !== 'none'
  const hasDates = !!cart?.starts_at && !!cart?.stops_at

  return { item, behavior, hasDates, useAvailability, cart }
}

const mapDispatchToProps = (dispatch, props) => ({
  fetchItem: () => dispatch(fetchItem(props.productId)),
  clearCart: () => CartActions.clear(),
  bookProduct: ({ id, quantity, configuration }) => CartActions.book({ item_id: id, quantity, configuration: { products: configuration } }),
  setVariationError: (id, value) => dispatch({ type: 'PRODUCT_VARIATION_ERROR', payload: { id, value } }),
  setBundleError: (id, value) => dispatch({ type: 'PRODUCT_BUNDLE_ERROR', payload: { id, value } })
})

const decoratedComponent = compose(
  withTranslation('user'),
  withTheme,
  withStore,
  reduxConnect(mapStateToProps, mapDispatchToProps)
)(ProductButton)

registerCustomElement('bq-product-button', decoratedComponent, {
  attributes: ['product-id']
})

export default decoratedComponent
